/**
 * @file pages/BlogDetail.jsx
 * @description World-class blog post page with @tailwindcss/typography prose,
 * table of contents, sticky social sharing sidebar, engagement suite (likes,
 * emoji reactions, nested comments), JSON-LD BlogPosting schema, and full SEO.
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from '../Context/Auth';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../Components/Layouts/Header';
import Footer from '../Components/Layouts/Footer';
import { FaCalendarAlt, FaClock, FaUser, FaTag, FaFacebook, FaTwitter, FaWhatsapp, FaLinkedin, FaLink, FaHeart, FaRegHeart, FaRegComment } from 'react-icons/fa';
import { FiArrowLeft, FiChevronUp } from 'react-icons/fi';
import { getBlog, getBlogComments, createBlogComment, getBlogReactions, toggleBlogReaction } from '../services/api';
import { sampleBlogs } from '../data/blogData';

// ─── Table of Contents ──────────────────────────────────────────
const TableOfContents = ({ headings, activeId }) => {
  if (!headings.length) return null;
  return (
    <nav className="hidden xl:block sticky top-28 w-56 self-start ml-8 shrink-0">
      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">On this page</p>
      <ul className="space-y-1.5 border-l-2 border-gray-200">
        {headings.map((h) => (
          <li key={h.id}>
            <a
              href={`#${h.id}`}
              className={`block pl-4 py-1 text-sm transition-all duration-200 border-l-2 -ml-[2px] ${
                activeId === h.id
                  ? 'text-[#243185] border-[#243185] font-medium'
                  : 'text-gray-400 border-transparent hover:text-gray-600 hover:border-gray-300'
              } ${h.level === 3 ? 'pl-7 text-xs' : ''}`}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
};

// ─── Sticky Social Sidebar ──────────────────────────────────────
const SocialSidebar = ({ blog, likesCount, liked, onLike }) => {
  const url = encodeURIComponent(window.location.href);
  const title = encodeURIComponent(blog?.title || '');

  const share = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${title}`,
      whatsapp: `https://wa.me/?text=${title}%20${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
    };
    window.open(urls[platform], '_blank', 'noopener,noreferrer');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
  };

  return (
    <div className="hidden lg:flex sticky top-28 flex-col items-center gap-3 mr-8 shrink-0">
      <button onClick={onLike} className="group relative flex flex-col items-center" title="Like this post">
        {liked ? (
          <FaHeart className="w-6 h-6 text-red-500 transition-transform group-hover:scale-110" />
        ) : (
          <FaRegHeart className="w-6 h-6 text-gray-400 group-hover:text-red-400 transition-all group-hover:scale-110" />
        )}
        <span className="text-xs text-gray-500 mt-1 font-medium">{likesCount}</span>
      </button>
      <div className="w-8 h-px bg-gray-200 my-1" />
      <button onClick={() => share('facebook')} className="p-2.5 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-all" title="Facebook">
        <FaFacebook className="w-5 h-5" />
      </button>
      <button onClick={() => share('twitter')} className="p-2.5 rounded-full hover:bg-sky-50 text-gray-400 hover:text-sky-500 transition-all" title="Twitter">
        <FaTwitter className="w-5 h-5" />
      </button>
      <button onClick={() => share('whatsapp')} className="p-2.5 rounded-full hover:bg-green-50 text-gray-400 hover:text-green-500 transition-all" title="WhatsApp">
        <FaWhatsapp className="w-5 h-5" />
      </button>
      <button onClick={() => share('linkedin')} className="p-2.5 rounded-full hover:bg-blue-50 text-gray-400 hover:text-blue-700 transition-all" title="LinkedIn">
        <FaLinkedin className="w-5 h-5" />
      </button>
      <button onClick={copyLink} className="p-2.5 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-all" title="Copy link">
        <FaLink className="w-5 h-5" />
      </button>
    </div>
  );
};

// ─── Floating Reaction Bar ──────────────────────────────────────
const ReactionBar = ({ reactions, userReactions, onToggle }) => {
  const reactionEmojis = [
    { type: 'like', emoji: '❤️', label: 'Like' },
    { type: 'fire', emoji: '🔥', label: 'Fire' },
    { type: 'love', emoji: '😍', label: 'Love' },
    { type: 'wow', emoji: '😮', label: 'Wow' },
  ];

  return (
    <div className="flex items-center justify-center gap-2 py-6">
      {reactionEmojis.map(({ type, emoji, label }) => {
        const active = userReactions.includes(type);
        const count = reactions[type] || 0;
        return (
          <button
            key={type}
            onClick={() => onToggle(type)}
            title={label}
            className={`group flex items-center gap-1.5 px-4 py-2.5 rounded-full border-2 transition-all duration-300 hover:scale-105 ${
              active
                ? 'border-[#243185]/30 bg-[#243185]/5 shadow-sm'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            }`}
          >
            <span className="text-xl group-hover:scale-125 transition-transform duration-200">{emoji}</span>
            {count > 0 && <span className="text-sm font-semibold text-gray-600">{count}</span>}
          </button>
        );
      })}
    </div>
  );
};

// ─── Comment Section ────────────────────────────────────────────
const CommentSection = ({ blogId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const res = await getBlogComments(blogId);
        const data = res.data?.data || res.data;
        setComments(data.comments || []);
        setTotal(data.total || 0);
      } catch (err) { console.error('Failed to load comments:', err); }
    };
    fetchComments();
  }, [blogId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    try {
      setSubmitting(true);
      await createBlogComment(blogId, { content: commentText, parentComment: replyTo });
      // Refresh comments
      const res = await getBlogComments(blogId);
      const data = res.data?.data || res.data;
      setComments(data.comments || []);
      setTotal(data.total || 0);
      setCommentText('');
      setReplyTo(null);
      setShowForm(false);
      toast.success('Comment posted!');
    } catch (err) {
      console.error('Failed to post comment:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to post comment';
      if (err.response?.status === 401 || err.response?.status === 403) {
        toast.error('Please log out and log back in to comment.');
      } else {
        toast.error(msg);
      }
    }
    finally { setSubmitting(false); }
  };

  const CommentItem = ({ comment, depth = 0 }) => (
    <div className={`${depth > 0 ? 'ml-8 pl-4 border-l-2 border-gray-100' : ''}`}>
      <div className="flex gap-3 py-4">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#243185] to-[#5365dc] flex items-center justify-center text-white text-sm font-bold shrink-0">
          {comment.author?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-gray-900 text-sm">{comment.author?.name || 'Anonymous'}</span>
            <span className="text-xs text-gray-400">
              {new Date(comment.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <p className="text-gray-600 text-sm mt-1 leading-relaxed">{comment.content}</p>
          {user && (
            <button
              onClick={() => { setReplyTo(comment._id); setShowForm(true); }}
              className="text-xs text-[#243185] font-medium mt-2 hover:underline"
            >
              Reply
            </button>
          )}
        </div>
      </div>
      {comment.replies?.map(reply => (
        <CommentItem key={reply._id} comment={reply} depth={depth + 1} />
      ))}
    </div>
  );

  return (
    <div className="mt-12 pt-8 border-t border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FaRegComment className="text-[#243185]" />
          Comments ({total})
        </h3>
        {user ? (
          <button
            onClick={() => { setShowForm(!showForm); setReplyTo(null); }}
            className="px-4 py-2 bg-[#243185] text-white text-sm font-medium rounded-lg hover:bg-[#1a2460] transition-colors"
          >
            Write a Comment
          </button>
        ) : (
          <button
            onClick={() => navigate('/login', { state: { from: { pathname: window.location.pathname } } })}
            className="px-4 py-2 bg-[#243185] text-white text-sm font-medium rounded-lg hover:bg-[#1a2460] transition-colors"
          >
            Login to Comment
          </button>
        )}
      </div>

      {showForm && user && (
        <form onSubmit={handleSubmit} className="bg-gray-50 rounded-xl p-6 mb-8 border border-gray-200">
          {replyTo && (
            <div className="flex items-center justify-between mb-3 text-sm">
              <span className="text-gray-500">Replying to a comment</span>
              <button type="button" onClick={() => setReplyTo(null)} className="text-red-500 hover:underline text-xs">Cancel reply</button>
            </div>
          )}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#243185] to-[#5365dc] flex items-center justify-center text-white text-sm font-bold shrink-0">
              {user.displayName?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <span className="text-sm font-medium text-gray-700">Commenting as <strong>{user.displayName || user.email}</strong></span>
          </div>
          <textarea placeholder="Write your comment..." required rows={4} value={commentText} onChange={(e) => setCommentText(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#243185]/20 focus:border-[#243185] outline-none resize-none mb-4" />
          <button type="submit" disabled={submitting}
            className="px-6 py-2.5 bg-[#243185] text-white text-sm font-medium rounded-lg hover:bg-[#1a2460] transition-colors disabled:opacity-50">
            {submitting ? 'Posting...' : 'Post Comment'}
          </button>
        </form>
      )}

      <div className="divide-y divide-gray-100">
        {comments.length > 0 ? comments.map(c => <CommentItem key={c._id} comment={c} />) : (
          <p className="text-gray-400 text-sm py-6 text-center">No comments yet. Be the first to share your thoughts!</p>
        )}
      </div>
    </div>
  );
};

// ─── Main Blog Detail Page ──────────────────────────────────────
const BlogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reactions, setReactions] = useState({ like: 0, fire: 0, love: 0, wow: 0 });
  const [userReactions, setUserReactions] = useState([]);
  const [headings, setHeadings] = useState([]);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Fetch blog post
  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        const response = await getBlog(id);
        const data = response.data?.data || response.data;
        if (data?.blog) {
          setBlog(data.blog);
          if (data.reactions) setReactions(prev => ({ ...prev, ...data.reactions }));
        } else {
          // Fallback to sample data
          const found = sampleBlogs.find(b => b._id === id || b.slug === id);
          if (found) setBlog(found);
          else navigate('/blogs');
        }
      } catch (error) {
        const found = sampleBlogs.find(b => b._id === id || b.slug === id);
        if (found) setBlog(found);
        else navigate('/blogs');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [id, navigate]);

  // Fetch reactions
  useEffect(() => {
    if (!blog?._id || typeof blog._id !== 'string' || !blog._id.match(/^[0-9a-fA-F]{24}$/)) return;
    const fetchReactions = async () => {
      try {
        const res = await getBlogReactions(blog._id);
        const data = res.data?.data || res.data;
        if (data?.summary) setReactions(data.summary);
        if (data?.userReactions) setUserReactions(data.userReactions);
      } catch (err) { /* ignore */ }
    };
    fetchReactions();
  }, [blog?._id]);

  // Extract headings from HTML content for TOC
  useEffect(() => {
    if (!contentRef.current) return;
    const els = contentRef.current.querySelectorAll('h2, h3');
    const extracted = Array.from(els).map((el, i) => {
      const elId = el.id || `heading-${i}`;
      el.id = elId;
      return { id: elId, text: el.textContent, level: parseInt(el.tagName[1]) };
    });
    setHeadings(extracted);
  }, [blog?.content]);

  // Scroll spy for TOC + scroll-to-top button
  useEffect(() => {
    const onScroll = () => {
      setShowScrollTop(window.scrollY > 600);
      if (!headings.length) return;
      let current = '';
      for (const h of headings) {
        const el = document.getElementById(h.id);
        if (el && el.getBoundingClientRect().top <= 120) current = h.id;
      }
      setActiveHeadingId(current);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [headings]);

  const handleReaction = async (type) => {
    if (!blog?._id || !blog._id.match?.(/^[0-9a-fA-F]{24}$/)) return;
    try {
      const res = await toggleBlogReaction(blog._id, { type });
      const action = res.data?.data?.action;
      setReactions(prev => ({
        ...prev,
        [type]: action === 'added' ? (prev[type] || 0) + 1 : Math.max(0, (prev[type] || 0) - 1)
      }));
      setUserReactions(prev =>
        action === 'added' ? [...prev, type] : prev.filter(r => r !== type)
      );
    } catch (err) { /* ignore */ }
  };

  // Process blog HTML content to fix spacing and images
  const processBlogContent = (html) => {
    if (!html || typeof html !== 'string') return html;
    // Convert empty paragraphs (<p><br></p>) to visible spacers
    let processed = html.replace(/<p>\s*<br\s*\/?\s*>\s*<\/p>/gi, '<div style="height:1.2em"></div>');
    // Also handle <p>&nbsp;</p> or <p></p>
    processed = processed.replace(/<p>(&nbsp;)<\/p>/gi, '<div style="height:1.2em"></div>');
    processed = processed.replace(/<p><\/p>/gi, '<div style="height:1.2em"></div>');
    return processed;
  };

  // Attach onerror to images in rendered content to hide broken ones
  useEffect(() => {
    if (!contentRef.current) return;
    const imgs = contentRef.current.querySelectorAll('img');
    imgs.forEach(img => {
      img.onerror = () => { img.style.display = 'none'; };
    });
  }, [blog?.content]);

  // Computed values
  const imageUrl = blog?.featuredImage?.url || blog?.image || 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1920&q=80';
  const authorName = blog?.authorName || (typeof blog?.author === 'object' ? blog?.author?.name : (blog?.author || 'Nikaba Travels'));
  const authorPhoto = blog?.authorPhoto || (typeof blog?.author === 'object' ? blog?.author?.photoURL : null);
  const readTime = blog?.readingTime || 5;
  const publishDate = blog?.publishDate || blog?.createdAt;
  const isHtmlContent = typeof blog?.content === 'string' && (blog.content.includes('<') || blog.content.includes('&'));

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#243185]"></div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex flex-col items-center justify-center py-40">
          <h2 className="text-2xl font-semibold text-gray-800">Blog post not found</h2>
          <Link to="/blogs" className="mt-4 px-6 py-2 bg-[#243185] text-white rounded-lg hover:bg-[#1a2460] transition-colors">
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{blog.seoTitle || blog.title} | Nikaba Travels</title>
        <meta name="description" content={blog.seoDescription || blog.excerpt} />
        <meta name="keywords" content={blog.tags?.join(', ') || ''} />
        <link rel="canonical" href={`https://nikabatravels.com/blogs/${blog.slug || blog._id}`} />
        <meta property="og:title" content={blog.seoTitle || blog.title} />
        <meta property="og:description" content={blog.seoDescription || blog.excerpt} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={`https://nikabatravels.com/blogs/${blog.slug || blog._id}`} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={publishDate} />
        <meta property="article:section" content={blog.category} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.seoTitle || blog.title} />
        <meta name="twitter:description" content={blog.seoDescription || blog.excerpt} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@nikabatravels" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blog.title,
            "description": blog.seoDescription || blog.excerpt,
            "image": imageUrl,
            "author": { "@type": "Person", "name": authorName },
            "publisher": {
              "@type": "Organization",
              "name": "Nikaba Travels",
              "logo": { "@type": "ImageObject", "url": "https://nikabatravels.com/assets/logo.png" }
            },
            "datePublished": publishDate,
            "dateModified": blog.updatedAt || publishDate,
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://nikabatravels.com/blogs/${blog.slug || blog._id}` },
            "wordCount": blog.content?.replace(/<[^>]*>/g, '').split(/\s+/).length || 0,
            "articleSection": blog.category,
            "keywords": blog.tags?.join(', ')
          })}
        </script>
      </Helmet>

      <Header />

      {/* Hero */}
      <div className="relative bg-gray-900 text-white pt-24">
        <div className="absolute inset-0">
          <img src={imageUrl} alt={blog.title} className="w-full h-full object-cover opacity-30" loading="eager"
            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=1920&q=80'; }} />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/50 to-gray-900/90" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 pt-16 pb-32 sm:px-6 lg:px-8">
          <Link to="/blogs" className="inline-flex items-center gap-2 text-blue-300 hover:text-white text-sm mb-8 transition-colors">
            <FiArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <div className="space-y-5">
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-[#243185]/80 backdrop-blur-sm text-white text-xs font-bold rounded-full">
                {blog.category || 'Travel Guide'}
              </span>
              <span className="px-3 py-1 bg-white/10 backdrop-blur-sm text-blue-200 text-xs font-medium rounded-full flex items-center gap-1">
                <FaClock className="w-3 h-3" /> {readTime} min read
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.15] tracking-tight">
              {blog.title}
            </h1>
            {blog.excerpt && (
              <p className="text-lg text-gray-300 leading-relaxed max-w-3xl">{blog.excerpt}</p>
            )}
            <div className="flex flex-wrap items-center gap-5 pt-2">
              <div className="flex items-center gap-2">
                {authorPhoto ? (
                  <img src={authorPhoto} alt={authorName} className="w-10 h-10 rounded-full object-cover border-2 border-white/30" onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }} />
                ) : null}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#243185] to-[#5365dc] flex items-center justify-center text-white text-sm font-bold" style={authorPhoto ? { display: 'none' } : {}}>
                  {authorName?.charAt(0)?.toUpperCase() || 'N'}
                </div>
                <div>
                  <p className="text-white font-medium text-sm">{authorName}</p>
                  <p className="text-gray-400 text-xs">
                    {new Date(publishDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-400">
                <span>👁 {blog.viewCount || 0} views</span>
                <span>❤️ {reactions.like || blog.likesCount || 0} likes</span>
                <span>💬 {blog.commentsCount || 0} comments</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 -mt-20 relative z-10 pb-16">
        <div className="flex justify-center">
          {/* Sticky Social Sidebar */}
          <SocialSidebar
            blog={blog}
            likesCount={reactions.like || blog.likesCount || 0}
            liked={userReactions.includes('like')}
            onLike={() => handleReaction('like')}
          />

          {/* Main Article */}
          <article className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-w-3xl w-full">
            <div className="p-6 sm:p-10 md:p-14">
              {/* Render HTML content with Tailwind Typography */}
              {isHtmlContent ? (
                <div
                  ref={contentRef}
                  className="prose prose-lg prose-gray max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-a:text-[#243185] prose-img:rounded-xl prose-img:shadow-lg prose-blockquote:border-[#243185] prose-blockquote:bg-blue-50/50 prose-blockquote:rounded-r-lg prose-blockquote:py-1 prose-pre:bg-gray-900 prose-hr:border-gray-200"
                  dangerouslySetInnerHTML={{ __html: processBlogContent(blog.content) }}
                />
              ) : (
                <div ref={contentRef} className="prose prose-lg prose-gray max-w-none">
                  {/* Legacy structured content support */}
                  {blog.content?.introduction && (
                    <p className="text-lg leading-relaxed">{blog.content.introduction}</p>
                  )}
                  {blog.content?.sections?.map((section, i) => (
                    <div key={i}>
                      <h2>{section.title}</h2>
                      {section.image && (
                        <figure>
                          <img src={section.image} alt={section.imageAlt || section.title} loading="lazy"
                            onError={(e) => { e.target.onerror = null; e.target.src = 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=800&q=80'; }} />
                          {section.imageAlt && <figcaption>{section.imageAlt}</figcaption>}
                        </figure>
                      )}
                      {section.content?.split('\n\n').map((p, j) => <p key={j}>{p}</p>)}
                    </div>
                  ))}
                  {blog.content?.conclusion && (
                    <blockquote>
                      <strong>Final Thoughts</strong><br />{blog.content.conclusion}
                    </blockquote>
                  )}
                </div>
              )}

              {/* Reaction Bar */}
              <div className="border-t border-gray-200 mt-10">
                <ReactionBar reactions={reactions} userReactions={userReactions} onToggle={handleReaction} />
              </div>

              {/* Tags */}
              {blog.tags?.length > 0 && (
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 mb-3">
                    <FaTag className="text-[#243185] w-4 h-4" />
                    <span className="font-bold text-gray-800 text-sm">Topics</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {blog.tags.map((tag, i) => (
                      <Link key={i} to={`/blogs?tag=${encodeURIComponent(tag)}`}
                        className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full text-xs font-medium hover:bg-[#243185]/10 hover:text-[#243185] transition-colors">
                        #{tag}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Author Details */}
              <div className="mt-10 pt-8 border-t border-gray-200">
                <div className="flex items-center gap-4 p-6 bg-gray-50 rounded-xl border border-gray-200">
                  {authorPhoto ? (
                    <img src={authorPhoto} alt={authorName || 'Author'}
                      className="w-16 h-16 rounded-full object-cover border-2 border-[#243185]/20 shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling && (e.target.nextElementSibling.style.display = 'flex'); }} />
                  ) : null}
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#243185] to-[#5365dc] flex items-center justify-center text-white text-xl font-bold shrink-0" style={authorPhoto ? { display: 'none' } : {}}>
                    {authorName?.charAt(0)?.toUpperCase() || 'N'}
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Written by</p>
                    <h4 className="text-lg font-bold text-gray-900">{authorName}</h4>
                    {blog?.author?.username && (
                      <p className="text-sm text-gray-500">@{blog.author.username}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Comments */}
              {blog._id && blog._id.match?.(/^[0-9a-fA-F]{24}$/) && (
                <CommentSection blogId={blog._id} />
              )}

              {/* Mobile Share Bar */}
              <div className="lg:hidden mt-10 pt-6 border-t border-gray-200">
                <p className="text-sm font-bold text-gray-700 mb-3">Share this story</p>
                <div className="flex gap-2">
                  {[
                    { icon: FaFacebook, color: 'hover:bg-blue-50 hover:text-blue-600', platform: 'facebook' },
                    { icon: FaTwitter, color: 'hover:bg-sky-50 hover:text-sky-500', platform: 'twitter' },
                    { icon: FaWhatsapp, color: 'hover:bg-green-50 hover:text-green-500', platform: 'whatsapp' },
                    { icon: FaLinkedin, color: 'hover:bg-blue-50 hover:text-blue-700', platform: 'linkedin' },
                  ].map(({ icon: Icon, color, platform }) => (
                    <button key={platform}
                      onClick={() => {
                        const u = encodeURIComponent(window.location.href);
                        const t = encodeURIComponent(blog.title);
                        const urls = { facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`, twitter: `https://twitter.com/intent/tweet?url=${u}&text=${t}`, whatsapp: `https://wa.me/?text=${t}%20${u}`, linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}` };
                        window.open(urls[platform], '_blank', 'noopener,noreferrer');
                      }}
                      className={`p-3 rounded-full border border-gray-200 text-gray-400 transition-all ${color}`}>
                      <Icon className="w-5 h-5" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {/* Table of Contents */}
          <TableOfContents headings={headings} activeId={activeHeadingId} />
        </div>
      </div>

      {/* Scroll to top */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 z-50 p-3 bg-[#243185] text-white rounded-full shadow-lg hover:bg-[#1a2460] transition-all hover:scale-110"
        >
          <FiChevronUp className="w-5 h-5" />
        </button>
      )}

      <Footer />

      {/* Blog content style overrides — Quill formatting + photo grids */}
      <style>{`
        /* Quill text alignment classes */
        .prose .ql-align-center { text-align: center; }
        .prose .ql-align-right { text-align: right; }
        .prose .ql-align-justify { text-align: justify; }
        .prose .ql-align-left { text-align: left; }
        /* Quill indent classes */
        .prose .ql-indent-1 { padding-left: 3em; }
        .prose .ql-indent-2 { padding-left: 6em; }
        .prose .ql-indent-3 { padding-left: 9em; }
        .prose .ql-indent-4 { padding-left: 12em; }
        /* Quill font sizes */
        .prose .ql-size-small { font-size: 0.75em; }
        .prose .ql-size-large { font-size: 1.5em; }
        .prose .ql-size-huge { font-size: 2.5em; }
        /* Ensure paragraph spacing is visible */
        .prose p {
          margin-top: 1.25em !important;
          margin-bottom: 1.25em !important;
        }
        .prose p:first-child { margin-top: 0 !important; }
        /* Ensure heading spacing */
        .prose h2, .prose h3 {
          margin-top: 2em !important;
          margin-bottom: 0.75em !important;
        }
        /* Photo grid containers */
        .prose [data-photo-grid],
        .prose p[style*="display:flex"],
        .prose p[style*="display: flex"],
        .prose div[style*="display:flex"],
        .prose div[style*="display: flex"] {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 8px !important;
          margin: 16px 0 !important;
        }
        .prose [data-photo-grid] img,
        .prose p[style*="display:flex"] img,
        .prose p[style*="display: flex"] img,
        .prose div[style*="display:flex"] img,
        .prose div[style*="display: flex"] img {
          display: block !important;
          margin: 0 !important;
          border-radius: 8px;
          object-fit: cover;
          flex-shrink: 0;
        }
        .prose img[style*="display:inline-block"],
        .prose img[style*="display: inline-block"] {
          display: inline-block !important;
          margin: 4px !important;
          vertical-align: top;
        }
        .prose img[src=""],
        .prose img:not([src]) {
          display: none !important;
        }
        .prose img {
          max-width: 100%;
        }
      `}</style>
    </div>
  );
};

export default BlogDetail;
